import { create } from 'zustand';

/**
 * Camera Store - Manages camera data and UI state
 * 
 * Usage:
 *   const cameras = useCameraStore(s => s.cameras);
 *   const viewMode = useCameraStore(s => s.viewMode);
 */
export const useCameraStore = create((set, get) => ({
  // ==========================================
  // DATA
  // ==========================================
  cameras: [],
  
  setCameras: (cameras) => set({ cameras }),
  
  addCamera: (camera) => set((state) => ({
    cameras: [...state.cameras, camera],
  })),
  
  updateCamera: (id, updates) => set((state) => ({
    cameras: state.cameras.map(cam => 
      cam.id === id ? { ...cam, ...updates } : cam
    ),
  })),
  
  removeCamera: (id) => set((state) => ({
    cameras: state.cameras.filter(cam => cam.id !== id),
  })),
  
  // ==========================================
  // STREAM STATUS (for live monitoring)
  // ==========================================
  streamStatus: {}, // { cameraId: 'online' | 'offline' | 'connecting' }
  
  updateStreamStatus: (cameraId, status) => set((state) => ({
    streamStatus: { ...state.streamStatus, [cameraId]: status },
  })),
  
  bulkUpdateStreamStatus: (statusMap) => set((state) => ({
    streamStatus: { ...state.streamStatus, ...statusMap },
  })),
  
  // ==========================================
  // UI STATE
  // ==========================================
  viewMode: 'list', // 'list', 'grid', 'wall'
  
  setViewMode: (mode) => set({ viewMode: mode }),
  
  // Search and filters
  search: '',
  filterStatus: '', // '', 'online', 'offline', 'inactive'
  
  setSearch: (search) => set({ search }),
  setFilterStatus: (status) => set({ filterStatus: status }),
  
  // Wall view density
  wallDensity: 'md', // 'sm', 'md', 'lg'
  setWallDensity: (density) => set({ wallDensity: density }),
  
  // ==========================================
  // SELECTION (for bulk operations)
  // ==========================================
  selectedCameraIds: [],
  
  toggleCameraSelection: (id) => set((state) => {
    const isSelected = state.selectedCameraIds.includes(id);
    return {
      selectedCameraIds: isSelected
        ? state.selectedCameraIds.filter(cid => cid !== id)
        : [...state.selectedCameraIds, id],
    };
  }),
  
  selectAll: () => set((state) => ({
    selectedCameraIds: state.cameras.map(c => c.id),
  })),
  
  clearSelection: () => set({ selectedCameraIds: [] }),
  
  // ==========================================
  // LOG VIEWER
  // ==========================================
  logCameraId: null,
  
  openLogViewer: (cameraId) => set({ logCameraId: cameraId }),
  closeLogViewer: () => set({ logCameraId: null }),
  
  // ==========================================
  // CAMERA DRAWER (side panel for details)
  // ==========================================
  drawerCameraId: null,
  drawerTab: 'live', // 'live', 'recordings', 'settings'
  
  openDrawer: (cameraId, tab = 'live') => set({
    drawerCameraId: cameraId,
    drawerTab: tab,
  }),
  
  closeDrawer: () => set({ drawerCameraId: null }),
  setDrawerTab: (tab) => set({ drawerTab: tab }),
  
  // ==========================================
  // COMPUTED VALUES / SELECTORS
  // ==========================================
  
  /**
   * Get filtered cameras based on search and filters
   */
  getFilteredCameras: () => {
    const { cameras, search, filterStatus, streamStatus } = get();
    
    return cameras.filter(camera => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch = 
          camera.name?.toLowerCase().includes(searchLower) ||
          camera.location?.toLowerCase().includes(searchLower) ||
          camera.ip_address?.includes(searchLower);
        
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filterStatus) {
        if (filterStatus === 'inactive' && camera.is_active) return false;
        if (filterStatus === 'online' && streamStatus[camera.id] !== 'online') return false;
        if (filterStatus === 'offline' && streamStatus[camera.id] !== 'offline') return false;
      }
      
      return true;
    });
  },
  
  /**
   * Get health metrics (for dashboard KPIs)
   */
  getHealthMetrics: () => {
    const { cameras, streamStatus } = get();
    
    const total = cameras.length;
    const inactive = cameras.filter(c => !c.is_active).length;
    const active = total - inactive;
    
    const online = cameras.filter(c => 
      c.is_active && streamStatus[c.id] === 'online'
    ).length;
    
    const offline = active - online;
    
    const healthScore = total > 0 
      ? Math.round((online / total) * 100)
      : 0;
    
    return {
      total,
      online,
      offline,
      inactive,
      healthScore,
    };
  },
}));
